import type { PwaInstallMethod, PwaInstallPlatform } from './types';

interface NavigatorUADataBrand {
  brand: string;
}

interface NavigatorUADataLike {
  brands?: NavigatorUADataBrand[];
  mobile?: boolean;
  platform?: string;
}

interface NavigatorWithInstallSignals extends Navigator {
  standalone?: boolean;
  userAgentData?: NavigatorUADataLike;
}

function getNavigator(): NavigatorWithInstallSignals | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  return navigator as NavigatorWithInstallSignals;
}

function getWindow(): Window | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window;
}

function getUserAgent(): string {
  return getNavigator()?.userAgent?.toLowerCase() ?? '';
}

function getVendor(): string {
  return getNavigator()?.vendor?.toLowerCase() ?? '';
}

function getBrands(): string[] {
  return (getNavigator()?.userAgentData?.brands ?? []).map(({ brand }) =>
    brand.toLowerCase(),
  );
}

function isTouchCapable(): boolean {
  const browserWindow = getWindow();
  const navigatorLike = getNavigator();

  return Boolean(
    (navigatorLike?.maxTouchPoints ?? 0) > 0 ||
    browserWindow?.matchMedia?.('(pointer: coarse)').matches,
  );
}

function hasAppleVendor(): boolean {
  return getVendor().includes('apple');
}

function hasChromiumBrand(): boolean {
  const brands = getBrands();
  if (
    brands.some(
      (brand) =>
        brand.includes('chromium') ||
        brand.includes('chrome') ||
        brand.includes('edge') ||
        brand.includes('opera'),
    )
  ) {
    return true;
  }

  const browserWindow = getWindow() as (Window & { chrome?: unknown }) | null;
  return Boolean(browserWindow?.chrome);
}

export function isStandalone(): boolean {
  const browserWindow = getWindow();
  const navigatorLike = getNavigator();

  const displayModeStandalone =
    browserWindow?.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone = navigatorLike?.standalone === true;
  const twaStandalone =
    typeof document !== 'undefined' &&
    document.referrer.startsWith('android-app://');

  return displayModeStandalone || iosStandalone || twaStandalone;
}

export function isIos(): boolean {
  const navigatorLike = getNavigator();
  const platform = navigatorLike?.platform?.toLowerCase() ?? '';
  const ua = getUserAgent();

  return (
    /iphone|ipad|ipod/.test(ua) ||
    /iphone|ipad|ipod/.test(platform) ||
    (platform === 'macintel' && hasAppleVendor() && isTouchCapable())
  );
}

export function isAndroid(): boolean {
  const navigatorLike = getNavigator();
  const ua = getUserAgent();
  const platform = navigatorLike?.userAgentData?.platform?.toLowerCase() ?? '';

  return platform === 'android' || ua.includes('android');
}

export function isFirefox(): boolean {
  const ua = getUserAgent();
  const brands = getBrands();

  return (
    brands.some((brand) => brand.includes('firefox')) || ua.includes('firefox')
  );
}

export function isChromiumBasedBrowser(): boolean {
  if (isFirefox()) {
    return false;
  }

  const ua = getUserAgent();
  return hasChromiumBrand() || ua.includes('chrome') || ua.includes('edg/');
}

export function isIosSafari(): boolean {
  const ua = getUserAgent();

  return (
    isIos() &&
    hasAppleVendor() &&
    !ua.includes('crios') &&
    !ua.includes('fxios') &&
    !ua.includes('edgios') &&
    !ua.includes('opios') &&
    !ua.includes('duckduckgo')
  );
}

export function getInstallPlatform(): PwaInstallPlatform {
  if (isStandalone()) {
    return 'standalone';
  }

  if (isIosSafari()) {
    return 'ios-safari';
  }

  if (isFirefox()) {
    return 'firefox';
  }

  if (isChromiumBasedBrowser() && isAndroid()) {
    return 'android-chromium';
  }

  if (isChromiumBasedBrowser()) {
    return 'desktop-chromium';
  }

  return 'unsupported';
}

export function isInstallSupported(): boolean {
  const platform = getInstallPlatform();
  return (
    platform === 'ios-safari' ||
    platform === 'android-chromium' ||
    platform === 'desktop-chromium'
  );
}

export function getInstallMethod(): PwaInstallMethod {
  const platform = getInstallPlatform();

  if (platform === 'ios-safari') {
    return 'manual';
  }

  if (platform === 'android-chromium' || platform === 'desktop-chromium') {
    return 'prompt';
  }

  return 'unsupported';
}
